import { VStack } from '@/components/ui/vstack';
import { Text } from '@/components/ui/text';
import { Pressable } from '@/components/ui/pressable';
import { HStack } from '@/components/ui/hstack';
import { Center } from '@/components/ui/center';
import { Box } from '@/components/ui/box';
import { Avatar, AvatarBadge } from '@/components/ui/avatar';
import { Badge } from "@/components/ui/badge";
import { createDrawerNavigator, DrawerContentScrollView } from '@react-navigation/drawer';
import i18n from 'i18n-js';
import { SafeAreaView } from 'react-native-safe-area-context';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Footer from '../components/basic/Footer';
import Header from '../components/basic/Header';
import ImageSynced from '../components/media/ImageSynced';
import Logout from '../screens/Logout';
import ModalAdmin from '../screens/modals/ModalAdmin';
import AccountSettings from '../screens/settings/AccountSettings';
import AppSettings from '../screens/settings/AppSettings';
import HelpScreen from '../screens/settings/HelpScreen';
import MyQRCode from '../screens/sharing/MyQRCode';
import BackGesture from './BackGesture';
import LegalStack from './LegalStack';
import MainStack from './MainStack';
import { switchScreen } from '../hooks/navigate';
import perfTrace from '../hooks/navigate';
import ModalMessage from '../screens/modals/ModalMessage';
import { IconM } from '../components/media/IconsV2';
import { storePerf } from '../stores/mainSlice';
import { Settings } from 'react-native';

const Drawer = createDrawerNavigator();

const getScreen = (route, navigation) => {
    switch (route.name) {
        case 'help':
            return <HelpScreen route={route} navigation={navigation} />;
        case 'admin':
            return <ModalAdmin route={route} navigation={navigation} />;
        case 'logout':
            return <Logout route={route} navigation={navigation} />;
        default:
            return null;
    }
};

function Component({ route, navigation }) {
    return (
        <BackGesture navigation={navigation}>
            <VStack space="xs" className="flex-1 bg-secondary-50">
                <Header navigation={navigation} options={{ title: i18n.t('sideNavigationDrawer.' + route.name) }} />
                {getScreen(route, navigation)}
                <Footer navigation={navigation} />
            </VStack>
        </BackGesture>
    );
}

const getIcon = (screenName) => {
    switch (screenName) {
        case 'MainStack':
            return 'home';
        case 'accountSettings':
            return 'person';
        case 'appSettings':
            return 'settings';
        case 'contactManagement':
            return 'group';
        case 'legal':
            return 'security';
        case 'help':
            return 'support-agent';
        case 'admin':
            return 'construction';
        case 'myQRCode':
            return 'qr-code-2';
        case 'logout':
            return 'logout';
        default:
            return undefined;
    }
};

function CustomDrawerContent(props) {
    const user = useSelector((state) => state.user);
    const server = useSelector((state) => state.server);
    const imgLoad = useSelector((state) => state.images.load);
    const notify = useSelector((state) => state.notify || {});

    useEffect(() => {}, [user.avatar, user.updated]);

    const isInContact = () => {
        const index = props.state?.routes[0]?.state?.routes.length - 1;
        const screenName = props.state?.routes[0]?.state?.routes[index]?.name;

        return screenName === 'ContactManagement' || screenName === 'Contacts' || screenName === 'Contact' || screenName === 'ContactsSelect' || screenName === 'SharedWorkflows' || screenName === 'Channels' || screenName === 'Channel' || screenName === 'Alternatives' || screenName === 'ScanQRCode';
    };

    const shouldHighlight = (index) => {
        return index === props.state.index || (index === 3 && isInContact());
    };

    return (
        <SafeAreaView className="bg-primary-900" edges={['top']}>
            <Box className={'h-full bg-secondary-50'}>
                <Center className="mt-0 bg-primary-900">
                    <VStack>
                        {user.avatar && (
                            <Box className={` ${server.online ? (server.url.startsWith('https://app.') ? 'border-[#00FF00]' : 'border-[#FF00FF]') : 'border-white'} border-[2px] rounded-full p-2 `}>
                                <Avatar>
                                    <ImageSynced key={user.avatar + '_' + imgLoad[user.avatar]?.duration + '_' + user.updated} uri={user.avatar} refresh={user.updated} isAvatar={true} className="border border-primary-900 bg-secondary-50" size="lg" />
                                    <AvatarBadge className={`-bottom-2 -right-2 ${notify.header ? 'border-secondary-50' : 'border-transparent'} ${notify.header ? 'bg-mark-900' : 'bg-transparent'} border `}></AvatarBadge>
                                </Avatar>
                            </Box>
                        )}
                    </VStack>
                    <Text className="font-[700] text-2xl leading-2xl text-secondary-50">{user.name}</Text>
                    <Text className="text-lg leading-xl mt-1 mb-2 text-secondary-50 font-[500]">{user.mail ? user.mail : ''}</Text>
                </Center>
                <DrawerContentScrollView contentContainerStyle={{ marginLeft: 0, paddingTop: 0, paddingLeft: 0, paddingRight: 0 }}>
                    <VStack space="sm" className="bg-primary-900">
                        <VStack space="lg" className="py-2 bg-secondary-50">
                            {props.state.routeNames.map(
                                (name, index) =>
                                    name != 'MainStack' &&
                                    name != 'ModalMessage' && (
                                        <Pressable
                                            key={name + index}
                                            onPress={() => {
                                                name == 'contactManagement' ? switchScreen(props.navigation, 'MainStack', { screen: 'ContactManagement' }) : switchScreen(props.navigation, name, {});
                                            }}
                                            className={` ${shouldHighlight(index) ? 'bg-primary-50' : 'bg-transparent'} px-4 py-3 `}>
                                            <HStack space="sm" className="items-center">
                                                <IconM className={` ${shouldHighlight(index) ? 'text-primary-900' : 'text-secondary-900'} text-3xl `} name={getIcon(name)} />
                                                <Text className={` ${shouldHighlight(index) ? 'text-primary-900' : 'text-secondary-900'} text-lg leading-xl `}>{i18n.t('sideNavigationDrawer.' + name)}</Text>
                                                {index == 3 && notify.contacts && <Badge className="absolute -top-1 left-8 px-0 bg-mark-900 border-secondary-50 border rounded-full w-3 h-3" />}
                                            </HStack>
                                        </Pressable>
                                    )
                            )}
                        </VStack>
                    </VStack>
                </DrawerContentScrollView>
            </Box>
        </SafeAreaView>
    );
}

export default function SideNavigationDrawer() {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    const settings = useSelector((state) => state.settings);
    return (
        <Drawer.Navigator
            screenOptions={{ headerShown: false, unmountOnBlur: true }}
            initialRouteName="MainStack"
            drawerContent={(props) => <CustomDrawerContent {...props} />}
            screenListeners={({ navigation }) => ({
                focus: (e) => {
                    if (settings.perf) {
                        const perf = perfTrace(navigation, 'OPN');
                        dispatch(storePerf({ data: perf }));
                    }
                },
                transitionEnd: (e) => {
                    if (settings.perf) {
                        const perf = perfTrace(navigation, e.data.closing ? 'CLS' : 'RDY');
                        dispatch(storePerf({ data: perf }));
                    }
                }
                // blur: (e) => {
                //     let screens = navigation.getState().routes.map(route => route.name)
                //     screens.pop()
                //     console.log('SCREEN END', screens.pop(), Date.now());
                // },
                // state: (e) => {
                //     console.log('SCREEN NAV', e.data.state.routes.map(route => route.name).pop(), Date.now());
                // },
            })}>
            <Drawer.Screen name="MainStack" component={MainStack} options={{ unmountOnBlur: false }} />
            {!user.demo && <Drawer.Screen name="accountSettings" component={AccountSettings} />}
            <Drawer.Screen name="appSettings" component={AppSettings} />
            <Drawer.Screen name="contactManagement" component={Component} />
            <Drawer.Screen name="myQRCode" component={MyQRCode} />
            <Drawer.Screen name="help" component={Component} />
            <Drawer.Screen name="legal" component={LegalStack} />
            {settings.preview && <Drawer.Screen name="admin" component={Component} />}
            <Drawer.Screen name="logout" component={Component} />
            <Drawer.Screen name="ModalMessage" component={ModalMessage} options={{ title: i18n.t('message.info') }} />
        </Drawer.Navigator>
    );
}
